import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography, debounce } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef } from '@mui/x-data-grid';

import { ListParams } from '../../../models/listParams';
import { PlanItem } from '../../../models/plan/planItem';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';

// import SharedOrganisatiesActionMenu from './SharedOrganisatiesActionMenu';

import { authService } from '../../../services/auth.service';
import { plannenService } from '../../../services/plannen.service';
import { helperService } from '../../../services/helper.service';
import { pageSizeStorageKey } from '../../../services/utils/localStorageKeys';
import { usePersistedPageSize } from '../../../hooks/usePersistedPageSize';

const CustomNoRowsOverlay = () => {
  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        padding: 2,
        backgroundColor: "transparent",
      }}
      elevation={0}
    >
      <Typography variant="body1">Niets gevonden</Typography>
    </Paper>
  );
};

interface SharedPlannenByOrganisatieProps {
  isZonderGebied: boolean;
  organisatieId: string;
}

const SharedPlannenByOrganisatie: React.FC<SharedPlannenByOrganisatieProps> = ({
  isZonderGebied,
  organisatieId
}) => {
  const navigate = useNavigate();

  const [sharedPlannen, setSharedPlannen] = useState<PlanItem[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  
  const [searchText, setSearchText] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('shared.plannen.byorganisatie'));
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const gridColumns = useMemo<(GridColDef<PlanItem> | GridActionsColDef<PlanItem>)[]>(() => {
    const baseColumns: (GridColDef<PlanItem> | GridActionsColDef<PlanItem>)[] = [];
    baseColumns.push({
      field: 'Discipline',
      headerName: 'Discipline',
      flex: 1,
      renderCell: (params: any) => {
        const camelDiscipline = helperService.toCamelCase(params.row.discipline);
        return (
          <Tooltip title={params.row.discipline} placement="right" arrow>
            <span className={`category-icon category-icon-small ${camelDiscipline}-icon-white`}>
              {/* <span>{camelDiscipline}</span> */}
            </span>
          </Tooltip>
        );
      }
    });

    baseColumns.push({
      field: 'planNaam', 
      headerName: 'Naam', 
      flex: 3 
    });

    // { field: 'name', headerName: 'Organisatie', flex: 2 },
    // baseColumns.push({
    //   field: 'actions',
    //   headerName: '',
    //   width: 70,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params: GridRenderCellParams<PlanItem>) => (
    //     <SharedOrganisatiesActionMenu params={params} planId={organisatieId} onOrganisatiesUpdate={fetchPlannenByOrganisatie} />
    //   ),
    // });

    return baseColumns;
  }, [organisatieId]);

  const getUserRole = async () => {
    const role = await authService.getRole();
    setUserRole(role);
  };

  const fetchPlannenByOrganisatie = useCallback(async () => {
    setLoading(true);
    try {
      const params: ListParams = {
        offset: pageIndex * pageSize,
        limit: pageSize,
        directSharedOnly: isZonderGebied,
        searchText: searchText?.trim() || undefined
      };

      let plannen: any[] = [];
      if (organisatieId) {
        const data = await plannenService.getSharedPlansByOrganisationId(organisatieId, params);
        plannen = data.items;
        plannen = plannen.map(p => ({
          ...p,
          id: p.infplId
        }));

        setSharedPlannen(plannen);
        setTotalCount(data.count);
      }
      else {
        setSharedPlannen([]);
      }

      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [pageIndex, pageSize, isZonderGebied, organisatieId, searchText, navigate]);

  useEffect(() => { 
    getUserRole(); 
    setReady(true); 
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchPlannenByOrganisatie();
  }, [ready, fetchPlannenByOrganisatie]);

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setSearchText(value);
      }, 100),
    []
  );

  const clearSearchText = async () => {
    setSearchText('');
  };

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPaginationModel({ page, pageSize });
    setPageIndex(page);
    setPageSize(pageSize);
  };

  return (
    <>
      <div className="grid-responsive">
        <div className="search"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "8px",
          }}>
          <TextField
            id="search"
            label="Zoeken in plannen"
            variant="filled"
            placeholder="Zoeken op plannaam"
            value={searchText ?? ""}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                fetchPlannenByOrganisatie(); // Zoek bij Enter
              }
            }}
            InputProps={{
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      clearSearchText();
                    }}
                    sx={{
                      p: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: '100%', md: '35%' }, 
              backgroundColor: "#fff", // witte achtergrond
              "& .MuiFilledInput-root": {
                backgroundColor: "#fff",
                borderRadius: "4px",
                "&:hover": { backgroundColor: "#fff" },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
              "& .MuiFilledInput-input": {
                paddingRight: "32px", // ruimte voor het clear-icoon
              },
            }}
          />
        </div>


        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={sharedPlannen}
            columns={gridColumns}
            disableColumnFilter
            disableColumnSorting
            disableRowSelectionOnClick
            loading={false}
            rowCount={totalCount}
            paginationMode="server"
            pageSizeOptions={[5, 10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  page: pageIndex,
                  pageSize: pageSize
                }
              }
            }}
            onPaginationModelChange={onChangePagination}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: () => null // or your own minimal custom component
            }}
            // localeText={{
            //   noRowsLabel: "Niets gevonden",
            // slots={{
            //   noRowsOverlay: CustomNoRowsOverlay,
            // }}
            sx={{ border: 0 }}
          />
        </Paper>
      </div>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default SharedPlannenByOrganisatie;
